import type { AiRankingPayload, AiTop10Change } from "@dashboard/shared";
import { AiPanel, AiTile } from "@/components/AiTile";
import { Skeleton } from "@/components/ui/skeleton";
import { recentChange } from "@/lib/aiRanking";

const SOURCE_URL = "https://artificialanalysis.ai/#intelligence";

const fmtScore = (n: number) =>
  new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);

export const fmtWhen = (iso: string) =>
  new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Vienna",
  }).format(new Date(iso));

const fmtDay = (iso: string) =>
  new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "Europe/Vienna" }).format(new Date(iso));

function ago(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.round(min / 60);
  return h < 48 ? `vor ${h} Std.` : `vor ${Math.round(h / 24)} Tagen`;
}

/** Eine Top-10-Aenderung als kurze deutsche Saetze. */
function describeChange(c: AiTop10Change): string[] {
  const lines: string[] = [];
  for (const m of c.entered) lines.push(`Neu auf Platz ${m.rank}: ${m.name} (${fmtScore(m.score)})`);
  for (const m of c.left) lines.push(`Raus: ${m.name} (vorher Platz ${m.prevRank})`);
  for (const m of c.moved) lines.push(`${m.name}: Platz ${m.from} → ${m.to}`);
  if (c.indexVersion && c.prevIndexVersion && c.indexVersion !== c.prevIndexVersion)
    lines.push(`Index-Version v${c.prevIndexVersion} → v${c.indexVersion}`);
  return lines;
}

const RED_PILL = "rounded-full bg-red-600 px-1.5 py-px text-[10px] font-semibold leading-none text-white";

/** NEU fuer Neuzugaenge, ▲/▼ nur fuer echte Ueberholer - nicht fuer Modelle, die ein
 *  Neuzugang bloss einen Platz nach unten schiebt (die stehen in keiner Meldung). */
function Marker({ slug, change }: { slug: string; change: AiTop10Change | null }) {
  if (!change) return null;
  if (change.entered.some((m) => m.slug === slug)) return <span className={RED_PILL}>NEU</span>;
  const mv = change.moved.find((m) => m.slug === slug);
  if (!mv) return null;
  return mv.to < mv.from ? (
    <span className="text-xs font-medium text-emerald-700">▲{mv.from - mv.to}</span>
  ) : (
    <span className="text-xs font-medium text-rose-700">▼{mv.to - mv.from}</span>
  );
}

/** Kachel rechts neben dem Digest: Nr. 1 und - solange hoechstens 7 Tage alt - die
 *  letzte Top-10-Aenderung als Kurzzeile (sonst zeigte das rote Badge ins Leere). */
export function AiRankingTile({
  data,
  fresh,
  open,
  onToggle,
}: {
  data: AiRankingPayload | null;
  fresh: Set<string>;
  open: boolean;
  onToggle: () => void;
}) {
  const leader = data?.models[0];
  const change = data ? recentChange(data) : null;
  return (
    <AiTile open={open} onToggle={onToggle} toggleId="ranking" title="KI-Top 20">
      {leader ? (
        // ohne Einstellungs-Klammer wie "(max with fallback)" - die Kachel ist schmal
        <span className="flex min-w-0 items-baseline gap-2 text-xs text-muted-foreground" title={`Platz 1: ${leader.name}`}>
          <span className="min-w-0 flex-1 truncate">{leader.name.replace(/\s*\(.*\)$/, "")}</span>
          <span className="shrink-0 font-semibold tabular-nums text-foreground">{fmtScore(leader.score)}</span>
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">{data ? "Noch kein Abruf" : "…"}</span>
      )}
      {change && (
        <span className="flex min-w-0 items-center gap-1.5 text-xs" data-ai-change-hint title={fmtWhen(change.at)}>
          {fresh.has(change.at) && <span className={RED_PILL}>neu</span>}
          <span className="truncate">geändert {fmtDay(change.at)}</span>
        </span>
      )}
    </AiTile>
  );
}

function RankRow({ m, change }: { m: AiRankingPayload["models"][number]; change: AiTop10Change | null }) {
  return (
    <li
      className="flex items-center gap-3 border-b border-border py-1.5 text-sm last:border-b-0"
      data-ai-rank={m.rank}
      title={m.creator ? `${m.name} · ${m.creator}` : m.name}
    >
      <span className="w-5 shrink-0 text-right tabular-nums text-muted-foreground">{m.rank}</span>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-medium">{m.name}</span>
        {m.creator && <span className="ml-2 text-xs text-muted-foreground">{m.creator}</span>}
      </span>
      {m.rank <= 10 && <Marker slug={m.slug} change={change} />}
      <span className="w-9 shrink-0 text-right font-semibold tabular-nums">{fmtScore(m.score)}</span>
    </li>
  );
}

/** Aufgeklappt: Aenderungsverlauf + Liste. Allein (volle Breite) stehen Platz 1-10 und
 *  11-20 nebeneinander, neben dem Kosten-Panel untereinander. */
export function AiRankingPanel({
  data,
  fresh,
  onClose,
  wide,
}: {
  data: AiRankingPayload | null;
  fresh: Set<string>;
  onClose: () => void;
  wide: boolean;
}) {
  const change = data ? recentChange(data) : null;
  const top = data?.models.filter((m) => m.rank <= 10) ?? [];
  const rest = data?.models.filter((m) => m.rank > 10) ?? [];
  return (
    <AiPanel
      panelId="ranking"
      title="KI-Modelle · Top 20 Intelligenz"
      onClose={onClose}
      description={
        <>
          {data?.checkedAt
            ? `Intelligence Index${data.indexVersion ? ` v${data.indexVersion}` : ""} · geprüft ${ago(data.checkedAt)} · Abruf 6, 14, 22 Uhr · `
            : "Noch kein Abruf - läuft um 6, 14 und 22 Uhr · "}
          <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">
            artificialanalysis.ai ↗
          </a>
        </>
      }
    >
      {!data ? (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-5" />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="mb-1.5 text-xs font-medium text-muted-foreground">Letzte Änderung in den Top 10</div>
            {data.changes.length ? (
              <ul className="space-y-2.5">
                {/* nur die neueste Aenderung (Operator 2026-10-01) */}
                {data.changes.slice(0, 1).map((c) => (
                  <li key={c.at} className="text-sm" data-ai-change={c.at}>
                    <div className="mb-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      {fresh.has(c.at) && <span className={RED_PILL}>neu</span>}
                      {fmtWhen(c.at)}
                    </div>
                    <ul className="space-y-0.5 leading-snug">
                      {describeChange(c).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">
                {data.since ? `Unverändert seit Beobachtungsbeginn (${fmtWhen(data.since)}).` : "Noch keine Daten."}
              </div>
            )}
          </div>

          <div className={wide ? "grid grid-cols-1 gap-x-8 lg:grid-cols-2" : ""}>
            <ol>
              {top.map((m) => (
                <RankRow key={m.slug} m={m} change={change} />
              ))}
            </ol>
            {rest.length > 0 && (
              <div>
                <div className={`flex items-center gap-2 pb-1 text-[11px] text-muted-foreground ${wide ? "lg:hidden" : ""} pt-3`}>
                  <span>Plätze 11–20</span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <ol start={11}>
                  {rest.map((m) => (
                    <RankRow key={m.slug} m={m} change={null} />
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </AiPanel>
  );
}
